/**
 * =============================================================================
 * ACCENT PALETTE SELECTOR - Change this to test different color schemes
 * =============================================================================
 * NOTE: Risk/Alignment colors use RAB system (Red/Amber/Blue - NOT green):
 *   - High/Danger/NotAligned: Red (#E14B4B)
 *   - Medium/Warning: Amber (#F0A326)
 *   - Low/Success/Aligned: Blue-teal (#19C3D6) - "passed checks" not "GO!"
 *
 * RAB colors are FIXED. These palettes only change ACCENT colors
 * (links, buttons, interactive elements, info status).
 *
 * Options (best fits for #144959 sidebar aesthetic):
 *   'current'    - Electric teal-cyan (#19C3D6) - current scheme
 *   'neptune'    - Cool ocean sky blue (#0EA5E9) - calming, professional
 *   'inked'      - Vibrant teal (#14B8A6) - modern edge, sophisticated
 *   'vichy'      - Fresh teal (#2DD4BF) - modern, calming, bit playful
 *   'sapphire'   - Deep ocean blue (#3B82F6) - authoritative, trustworthy
 *   'opaline'    - Minimalist + cinnabar (#E85D04) - warm accent, modern
 *   'clockwork'  - Gray base + orange (#EA580C) - balanced warmth
 */
export type AccentPalette =
  | 'current'
  | 'neptune'
  | 'inked'
  | 'vichy'
  | 'sapphire'
  | 'opaline'
  | 'clockwork';

const DEFAULT_ACCENT_PALETTE: AccentPalette = 'inked';

export const ACCENT_PALETTE_OPTIONS: {
  value: AccentPalette;
  label: string;
  description: string;
}[] = [
  { value: 'current', label: 'Electric Teal', description: 'Vibrant, modern' },
  {
    value: 'neptune',
    label: 'Neptune Blue',
    description: 'Calming, professional',
  },
  {
    value: 'inked',
    label: 'Inked Teal',
    description: 'Sophisticated, modern edge',
  },
  { value: 'vichy', label: 'Vichy Teal', description: 'Fresh, bit playful' },
  {
    value: 'sapphire',
    label: 'Sapphire Blue',
    description: 'Authoritative, trustworthy',
  },
  { value: 'opaline', label: 'Cinnabar Orange', description: 'Warm, modern' },
  {
    value: 'clockwork',
    label: 'Clockwork Orange',
    description: 'Balanced warmth',
  },
];

/**
 * RAB System Colors (FIXED - do not change per palette):
 * Blue for low/success/aligned (colorblind safe, "passed checks" not "GO!")
 */
const RAB_COLORS = {
  low: '#19C3D6',
  lowBg: 'rgba(25,195,214,0.18)',
  success: '#19C3D6',
  successBg: 'rgba(25,195,214,0.15)',
  aligned: '#19C3D6',
  alignedBg: 'rgba(25,195,214,0.18)',
  medium: '#F0A326',
  mediumBg: 'rgba(240,163,38,0.15)',
  high: '#E14B4B',
  highBg: 'rgba(225,75,75,0.15)',
} as const;

const ACCENT_PALETTES = {
  // Current: Electric teal-cyan - vibrant, modern
  current: {
    accent: '#19C3D6',
    accentDark: '#0E8A99',
    accentHover: '#0A6F7C',
    accentBg: 'rgba(25,195,214,0.08)',
    accentBgHover: 'rgba(25,195,214,0.12)',
    info: '#19C3D6',
    infoBg: 'rgba(25,195,214,0.12)',
    chartPrimary: '#19C3D6',
  },
  // Neptune (#34): Cool ocean blues - calming, professional, serene
  neptune: {
    accent: '#0EA5E9',
    accentDark: '#0284C7',
    accentHover: '#0369A1',
    accentBg: 'rgba(14,165,233,0.08)',
    accentBgHover: 'rgba(14,165,233,0.12)',
    info: '#0EA5E9',
    infoBg: 'rgba(14,165,233,0.12)',
    chartPrimary: '#0EA5E9',
  },
  // Inked (#51): Classic with vibrant teal - sophisticated, modern edge
  inked: {
    accent: '#14B8A6',
    accentDark: '#0D9488',
    accentHover: '#0F766E',
    accentBg: 'rgba(20,184,166,0.08)',
    accentBgHover: 'rgba(20,184,166,0.12)',
    info: '#14B8A6',
    infoBg: 'rgba(20,184,166,0.12)',
    chartPrimary: '#14B8A6',
  },
  // Vichy (#9): Fresh teal - modern, calming, bit playful
  vichy: {
    accent: '#2DD4BF',
    accentDark: '#14B8A6',
    accentHover: '#0D9488',
    accentBg: 'rgba(45,212,191,0.08)',
    accentBgHover: 'rgba(45,212,191,0.12)',
    info: '#2DD4BF',
    infoBg: 'rgba(45,212,191,0.12)',
    chartPrimary: '#2DD4BF',
  },
  // Sapphire (#23): Deep ocean blue - authoritative, trustworthy
  sapphire: {
    accent: '#3B82F6',
    accentDark: '#2563EB',
    accentHover: '#1D4ED8',
    accentBg: 'rgba(59,130,246,0.08)',
    accentBgHover: 'rgba(59,130,246,0.12)',
    info: '#3B82F6',
    infoBg: 'rgba(59,130,246,0.12)',
    chartPrimary: '#3B82F6',
  },
  // Opaline (#44): Minimalist with warm cinnabar pop - modern, sophisticated
  opaline: {
    accent: '#E85D04',
    accentDark: '#C2410C',
    accentHover: '#9A3412',
    accentBg: 'rgba(232,93,4,0.08)',
    accentBgHover: 'rgba(232,93,4,0.12)',
    info: '#19C3D6', // Keep teal-blue for info (RAB consistency)
    infoBg: 'rgba(25,195,214,0.12)',
    chartPrimary: '#E85D04',
  },
  // Clockwork (#46): Gray base with warm orange - balanced, cozy minimalism
  clockwork: {
    accent: '#EA580C',
    accentDark: '#C2410C',
    accentHover: '#9A3412',
    accentBg: 'rgba(234,88,12,0.08)',
    accentBgHover: 'rgba(234,88,12,0.12)',
    info: '#19C3D6', // Keep teal-blue for info (RAB consistency)
    infoBg: 'rgba(25,195,214,0.12)',
    chartPrimary: '#EA580C',
  },
} as const;

export function getAccentColors(
  accent: AccentPalette = DEFAULT_ACCENT_PALETTE
) {
  return ACCENT_PALETTES[accent];
}

export const Color = {
  // Base colors - New palette
  black: '#000000',
  white: '#ffffff',

  // Gray scale
  gray1: '#767676', // Black 118,118,118
  gray2: '#b2b2b2', // Gray no 1 178,178,178
  gray3: '#d9d9d9', // Gray no 2 217,217,217
  gray4: '#efefef', // Gray no 3 239,239,239

  // Ocean/Sea colors
  deepDown: '#144959', // Deep down 20,73,89
  deepSea: '#45879a', // Deep sea 38,100,118
  ocean: '#8fc4d3', // Ocean 69,135,154
  wind: '#8fc4d3', // Wind 143,196,211
  breeze: '#afddea', // Breeze 175,221,234
  puff: '#cbeffa', // Puff 203,239,250

  // Peach colors
  darkPeach: '#e37d7d', // Dark peach 227,125,125
  peach: '#f4a799', // Peach 244,167,153
  babyPeach: '#8fc4d3', // Baby peach 143,196,211
  lightPeach: '#fde7dc', // Light peach 253,231,220

  // Olive colors
  olive: '#a1cba6', // Olive 161,203,166
  brightOlive: '#b6ddba', // Bright olive 182,221,186

  // Primary colors
  red: '#fc6262', // Red 252,98,98
  orange: '#ffa65a', // Orange 255,166,90
  yellow: '#ffe96f', // Yellow 255,233,111
  green: '#6ebc77', // Green 110,188,119
  greenLight: '#dcfce7', // Light green for backgrounds
  greenDark: '#16a34a', // Dark green for badges/accents

  // Light variants (for backgrounds)
  redLight: '#fee2e2',
  orangeLight: '#fff7ed',
  yellowLight: '#fefce8',

  // Empty/disabled state
  grayEmpty: '#e5e7eb',

  // Legacy colors (keeping for backward compatibility)
  blue: '#006CD7',
  powderBlue: '#afddea',
  lightBlue: '#337CCF',
  medBlack: '#1E1E1E',
  grey: '#354649',
  riskMediumAmber: '#F0A326', // Amber for medium risk / pending (RAB)
  lightGrey: '#6C7A89',
  blueBlack: '#111827',

  // UI colors
  surfaceGray: '#F9FAFB',
  borderGray: '#F3F4F6',
  borderBlue: '#f9fafb',
  darkBlue: '#191D88',
  lightGreen: '#2AAF74',
  lightRed: '#EE8571',
  lightGray: '#f1f1f1',
  lightLight: '#e1dede',
  lightDark: '#201f1f',

  // Special shades
  shadeOfGreen: '#6ef2bb',
  shadeOfBlue: '#2253ff',
  warmOrange: '#ED904F',
  lightBlueHover: '#1565C00D',

  // Navigation
  navItemText: '#6B7280',
  navItemActiveText: '#0B72C1',
  navItemActiveBg: '#9FD2F9',
  navItemActiveTextDark: '#085691',

  // Form
  formLabelText: '#374151',

  // Policy status
  policyAlignedBg: '#D1FAE5',
  policyNotAlignedBg: '#FEE2E2',
  policyAlignedText: '#065F46',
  policyNotAlignedText: '#991B1B',

  // Risk levels - Updated with new colors
  riskHighBg: '#fde7dc', // Light peach background
  riskHighText: '#e37d7d', // Dark peach text
  riskMediumBg: '#f4a799', // Peach background
  riskMediumText: '#fc6262', // Red text
  riskLowBg: '#cbeffa', // Puff background
  riskLowText: '#45879a', // Deep sea text

  // input text field
  textFieldBorderColor: '#D1D5DB',
  textFieldInputTextColor: '#6B7280',
  textFieldLabelColor: '#18191f',
};

/** Light mode status tokens - deep cordovan for luxury (not emergency red) */
const STATUS_TOKENS = {
  success: '#0F766E',
  successBg: 'rgba(15,118,110,0.12)',
  warning: '#B45309',
  warningBg: 'rgba(180,83,9,0.14)',
  danger: '#5C2828',
  dangerBg: 'rgba(92,40,40,0.12)',
  info: '#19C3D6',
  infoBg: 'rgba(25,195,214,0.14)',
  neutral: '#64748B',
  neutralBg: 'rgba(100,116,139,0.12)',
} as const;

const RISK_TOKENS = {
  high: '#5C2828',
  highBg: 'rgba(92,40,40,0.14)',
  medium: '#B7791F',
  mediumBg: 'rgba(183,121,31,0.16)',
  low: '#0E7490',
  lowBg: 'rgba(14,116,144,0.14)',
} as const;

const POLICY_TOKENS = {
  alignedText: '#0E7490',
  alignedBg: 'rgba(14,116,144,0.14)',
  notAlignedText: '#5C2828',
  notAlignedBg: 'rgba(92,40,40,0.14)',
} as const;

/** Dark mode policy tokens - uses fixed RAB colors */
const POLICY_TOKENS_DARK = {
  alignedText: RAB_COLORS.aligned,
  alignedBg: RAB_COLORS.alignedBg,
  notAlignedText: RAB_COLORS.high,
  notAlignedBg: RAB_COLORS.highBg,
} as const;

const CHART_PALETTE = [
  '#9CA3AF', // Neutral gray (base segments)
  '#6B7280', // Darker neutral
  '#5A6B73', // Slate-gray (key positive - muted, not teal)
  '#5C2828', // Deep cordovan (key negative)
  '#B7791F', // Amber (warning)
  '#D1D5DB', // Light gray
] as const;

function getChartPaletteDark(
  selectedAccent: ReturnType<typeof getAccentColors>
) {
  return [
    selectedAccent.chartPrimary, // Primary accent - for non-RAB chart series
    RAB_COLORS.low, // Low risk / success (fixed blue)
    RAB_COLORS.medium, // Medium risk / warning (fixed amber)
    RAB_COLORS.high, // High risk / danger (fixed red)
    '#7B8794', // Neutral / unverified (muted gray)
    '#5B8DBF', // Secondary blue
    '#A9B4BF', // Light muted
    '#4A5568', // Dark muted
  ] as const;
}

const BRAND_TOKENS = {
  primary: '#144959',
  primaryHover: '#0E3D4B',
  primaryActive: '#0A3340',
  primaryContrast: '#FFFFFF',
} as const;

const NAV_TOKENS = {
  itemText: '#46515C',
  itemTextOnDark: 'rgba(247,245,242,0.88)',
  badgeMutedBg: 'rgba(255,255,255,0.16)',
  activeText: '#144959',
  activeBg: 'rgba(20,73,89,0.12)',
  activeTextDark: '#0A3340',
  // Light-mode equivalents for dark-only keys (sidebar uses dark teal bg even in light mode)
  sidebarBg: '#144959',
  sidebarBgElevated: '#0E3D4B',
  itemTextOnDarkMuted: 'rgba(247,245,242,0.60)',
  itemTextOnDarkHover: '#F7F5F2',
  activeBgDark: 'rgba(255,255,255,0.10)',
  activeIndicator: '#19C3D6',
  badgeTextOnDark: 'rgba(247,245,242,0.88)',
  sidebarDivider: 'rgba(255,255,255,0.06)',
  sidebarGradient: 'none',
  sidebarInset: 'none',
} as const;

/** Dark mode nav tokens - Blackstone/Palantir elegance sidebar */
const NAV_TOKENS_DARK = {
  ...NAV_TOKENS,
  // Near-black with subtle blue-green cast (NOT teal, NOT pure black)
  sidebarBg: '#0B0D10',
  sidebarBgElevated: '#10141A',
  // Soft off-white text hierarchy
  itemTextOnDark: '#E7EDF2',
  itemTextOnDarkMuted: '#A9B4BF',
  itemTextOnDarkHover: '#FFFFFF',
  // Subtle active state (not big pill) - remove teal tint
  activeBgDark: 'rgba(255,255,255,0.06)',
  // Electric teal-cyan for active indicator bar
  activeIndicator: '#19C3D6',
  // Badge styling for dark mode
  badgeMutedBg: 'rgba(255,255,255,0.08)',
  badgeTextOnDark: '#E7EDF2',
  // Ultra subtle dividers
  sidebarDivider: 'rgba(255,255,255,0.04)',
  // No gradient, minimal inset
  sidebarGradient: 'none',
  sidebarInset: 'inset 0 1px 0 rgba(255,255,255,0.03)',
} as const;

/** Dark mode risk tokens - uses fixed RAB colors (Red/Amber/Blue) */
const RISK_TOKENS_DARK = {
  high: RAB_COLORS.high,
  highBg: RAB_COLORS.highBg,
  medium: RAB_COLORS.medium,
  mediumBg: RAB_COLORS.mediumBg,
  low: RAB_COLORS.low,
  lowBg: RAB_COLORS.lowBg,
} as const;

/** Dark mode status tokens - RAB for success/warning/danger, accent for info */
function getStatusTokensDark(
  selectedAccent: ReturnType<typeof getAccentColors>
) {
  return {
    success: RAB_COLORS.success,
    successBg: RAB_COLORS.successBg,
    warning: RAB_COLORS.medium,
    warningBg: RAB_COLORS.mediumBg,
    danger: RAB_COLORS.high,
    dangerBg: RAB_COLORS.highBg,
    info: selectedAccent.info, // Info uses accent palette (not semantic RAB)
    infoBg: selectedAccent.infoBg,
    neutral: '#7B8794',
    neutralBg: 'rgba(255,255,255,0.06)',
  } as const;
}

export type DesignTokenSet = ReturnType<typeof getDesignTokens>;

/**
 * Returns theme-aware design tokens for the given mode and accent palette.
 * Use in theme creation and via useDesignTokens() for components.
 */
export function getDesignTokens(
  mode: 'light' | 'dark',
  accent: AccentPalette = DEFAULT_ACCENT_PALETTE
): {
  brand: typeof BRAND_TOKENS;
  canvasBg: string;
  canvasTexture: string;
  surface1: string;
  surface2: string;
  surfaceGradient1: string;
  surfaceOverlay: string;
  hairline: string;
  borderSubtle: string;
  borderDefault: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textPlaceholder: string;
  status: typeof STATUS_TOKENS | typeof STATUS_TOKENS_DARK;
  shadowSoft: string;
  shadowMedium: string;
  radiusSmall: number;
  radiusMedium: number;
  radiusLarge: number;
  radiusPill: number;
  risk: typeof RISK_TOKENS | typeof RISK_TOKENS_DARK;
  policy: typeof POLICY_TOKENS | typeof POLICY_TOKENS_DARK;
  nav: typeof NAV_TOKENS | typeof NAV_TOKENS_DARK;
  form: {
    labelText: string;
    inputText: string;
    inputLabel: string;
    borderColor: string;
  };
  link: string;
  linkHover: string;
  accent: string;
  accentHover: string;
  accentBg: string;
  accentContrast: string;
  buttonPrimary: string;
  buttonPrimaryHover: string;
  buttonPrimaryText: string;
  emptyBg: string;
  chart: { palette: readonly string[] };
  iconMuted: string;
  iconInteractive: string;
  accentBorder: string;
  focusRing: string;
  badgeAccent: string;
  badgeAccentContrast: string;
  avatarBg: string;
  avatarText: string;
} {
  const selectedAccent = getAccentColors(accent);
  const STATUS_TOKENS_DARK = getStatusTokensDark(selectedAccent);
  const CHART_PALETTE_DARK = getChartPaletteDark(selectedAccent);

  if (mode === 'dark') {
    return {
      brand: BRAND_TOKENS,
      // Basalt/graphite canvas - near-black, slightly warm-neutral
      canvasBg: '#111315',
      // Subtle grain/linen texture at ~3% opacity for premium stone feel (SVG noise)
      canvasTexture: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      // Charcoal card surfaces
      surface1: '#191B1E',
      // Subtle secondary surface
      surface2: '#1F2124',
      // Solid surface with faint top highlight (no gradient)
      surfaceGradient1: 'none',
      surfaceOverlay: 'rgba(255,255,255,0.02)',
      // Faint top inset highlight
      hairline: 'rgba(255,255,255,0.06)',
      // Hairline borders at 6-8% white
      borderSubtle: 'rgba(255,255,255,0.06)',
      borderDefault: 'rgba(255,255,255,0.08)',
      // Softer text for reading comfort - avoid pure white
      textPrimary: '#E8EAED',
      textSecondary: '#9CA3AF',
      textMuted: '#6B7280',
      textPlaceholder: '#4B5563',
      status: STATUS_TOKENS_DARK,
      // Soft shadows for subtle elevation
      shadowSoft: '0 1px 3px rgba(0,0,0,0.40), 0 4px 12px rgba(0,0,0,0.30)',
      shadowMedium: '0 8px 24px rgba(0,0,0,0.50)',
      radiusSmall: 8,
      radiusMedium: 12,
      radiusLarge: 16,
      radiusPill: 999,
      risk: RISK_TOKENS_DARK,
      policy: POLICY_TOKENS_DARK,
      nav: {
        ...NAV_TOKENS_DARK,
        activeIndicator: selectedAccent.accent,
      } as unknown as typeof NAV_TOKENS_DARK,
      form: {
        labelText: '#E8EAED',
        inputText: '#9CA3AF',
        inputLabel: '#E8EAED',
        borderColor: 'rgba(255,255,255,0.08)',
      },
      // Primary accent (uses selected palette)
      link: selectedAccent.accent,
      linkHover: selectedAccent.accentBgHover,
      // Unified accent color for interactive elements, progress, checkmarks
      accent: selectedAccent.accent,
      accentHover: selectedAccent.accentBgHover,
      accentBg: selectedAccent.accentBg,
      accentContrast: '#FFFFFF',
      // Premium button styling - deeper shade for better text contrast
      buttonPrimary: selectedAccent.accentDark,
      buttonPrimaryHover: selectedAccent.accentHover,
      buttonPrimaryText: '#FFFFFF',
      emptyBg: 'rgba(255,255,255,0.04)',
      chart: { palette: [...CHART_PALETTE_DARK] },
      iconMuted: selectedAccent.accent,
      iconInteractive: selectedAccent.accent,
      accentBorder: selectedAccent.accent,
      focusRing: `0 0 0 2px ${selectedAccent.accentHover}, 0 0 8px ${selectedAccent.accentBg}`,
      badgeAccent: selectedAccent.accent,
      badgeAccentContrast: BRAND_TOKENS.primaryContrast,
      avatarBg: selectedAccent.accent,
      avatarText: BRAND_TOKENS.primaryContrast,
    };
  }

  return {
    brand: BRAND_TOKENS,
    // Tom Ford Linen: barely warm linen canvas (luxury, not medical-clean)
    canvasBg: '#F4F3F0',
    // Subtle linen weave texture (coarser grain than dark mode charcoal)
    canvasTexture: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.45' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
    surface1: '#FFFFFF',
    surface2: '#F5F4F1',
    surfaceGradient1:
      'linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(244,246,248,0.94) 100%)',
    surfaceOverlay: 'rgba(15,23,42,0.018)',
    hairline: 'rgba(15,23,42,0.06)',
    // Tailored seam borders anchored to ink
    borderSubtle: 'rgba(15,23,42,0.08)',
    borderDefault: 'rgba(15,23,42,0.14)',
    // Richer near-ink text for "black tie" readability
    textPrimary: '#111827',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    textPlaceholder: '#9AA6B2',
    status: STATUS_TOKENS_DARK,
    // Quiet luxury shadows
    shadowSoft: '0 1px 2px rgba(15,23,42,0.06), 0 2px 10px rgba(15,23,42,0.04)',
    shadowMedium: '0 10px 28px rgba(15,23,42,0.10)',
    radiusSmall: 8,
    radiusMedium: 12,
    radiusLarge: 16,
    radiusPill: 999,
    risk: RISK_TOKENS_DARK,
    policy: POLICY_TOKENS_DARK,
    nav: {
      ...NAV_TOKENS,
      activeIndicator: selectedAccent.accent,
    } as unknown as typeof NAV_TOKENS,
    form: {
      labelText: '#111827',
      inputText: '#111827',
      inputLabel: '#111827',
      borderColor: 'rgba(15,23,42,0.16)',
    },
    // Deeper link for AA contrast on white; accent uses selected palette
    link: selectedAccent.accentDark,
    linkHover: selectedAccent.accentBgHover,
    accent: selectedAccent.accent,
    accentHover: selectedAccent.accentBgHover,
    accentBg: selectedAccent.accentBg,
    accentContrast: '#FFFFFF',
    // Aligned with accent family
    buttonPrimary: selectedAccent.accentDark,
    buttonPrimaryHover: selectedAccent.accentHover,
    buttonPrimaryText: '#FFFFFF',
    emptyBg: '#E7ECF2',
    chart: { palette: [...CHART_PALETTE_DARK] },
    iconMuted: selectedAccent.accent,
    iconInteractive: selectedAccent.accent,
    accentBorder: selectedAccent.accent,
    focusRing: `0 0 0 2px ${selectedAccent.accentHover}, 0 0 8px ${selectedAccent.accentBg}`,
    badgeAccent: selectedAccent.accent,
    badgeAccentContrast: BRAND_TOKENS.primaryContrast,
    avatarBg: selectedAccent.accent,
    avatarText: BRAND_TOKENS.primaryContrast,
  };
}

/**
 * Semantic design tokens for Phase 1 reskin (light mode).
 * Use these for theme and new components; Color remains for backward compatibility.
 * For theme-aware tokens, use getDesignTokens(mode) or useDesignTokens().
 */
export const DesignToken = getDesignTokens('light');
